/**
 * shopThumbnail.ts — Resilient, multi-source thumbnail resolution for shops and scans.
 *
 * Ordering matters more than breadth here. Every candidate that misses costs a
 * full round trip before the next one is tried, so the list is ordered cheapest-
 * and-most-certain first:
 *
 *   1. A URL the shop record already tells us (explicit poster, section thumbnail)
 *   2. The /poster API — one request, resolved server-side, cached for a day
 *   3. Blind blob-layout guesses, capped to the first couple of sections
 *   4. Dataset-derived frames
 *
 * The guesses used to come before /poster and were emitted for *every* section,
 * so a 16-section mall fired ~80 failing requests before reaching the endpoint
 * that always works — which is why big shops showed a blank card while small
 * ones loaded instantly.
 */

import { posterUrl, resolveUrl, toBlobUrl } from '@/config'
import { useShopStore } from '@/stores/shopStore'

/**
 * How many sections to blind-guess blob paths for.
 *
 * These are a last resort behind /poster, and the first section is almost always
 * the one with a usable frame. Guessing every section is what made the cost grow
 * with shop size.
 */
const MAX_GUESSED_SECTIONS = 2

const FRAMES_CONTAINER = 'https://vshopship.blob.core.windows.net/scan-frames'

export interface ThumbnailCandidateShop {
  id: string
  posterUrl?: string
  sections?: Array<{
    sectionId: string
    posterFrameIndex?: number
    thumbnailUrl?: string
    camerasJsonUrl?: string
  }>
  dataset?: any
}

/**
 * Returns an ordered array of candidate image URLs to try for a shop.
 */
export function getShopThumbnailCandidates(shop: ThumbnailCandidateShop): string[] {
  const candidates: string[] = []
  const storeShop = useShopStore.getState().publicShops.find((s) => s.id === shop.id)
  const sections = (shop.sections && shop.sections.length > 0) ? shop.sections : (storeShop?.sections ?? [])

  // 1. URLs the record already knows — no guessing involved.
  const poster = shop.posterUrl || storeShop?.posterUrl
  if (poster) {
    candidates.push(toBlobUrl(resolveUrl(poster)))
  }
  for (const sec of sections) {
    if (sec.thumbnailUrl) {
      candidates.push(toBlobUrl(resolveUrl(sec.thumbnailUrl)))
    }
  }

  // 2. The poster API. It walks the sections server-side and answers with the
  //    real frame, so it succeeds where the guesses below are only hoping. One
  //    request, and the response is cached for a day.
  candidates.push(posterUrl(shop.id))

  // 3. Blind blob-layout guesses — only for the first few sections.
  for (const sec of sections.slice(0, MAX_GUESSED_SECTIONS)) {
    const idx = typeof sec.posterFrameIndex === 'number' && sec.posterFrameIndex > 0 ? sec.posterFrameIndex : 1
    const pad4 = String(idx).padStart(4, '0')
    const pad1 = '0001'
    const pad0 = '0000'

    // Web scan v1 layout: scan-frames/{shopId}/{sectionId}/v1/frame_XXXX.jpg
    candidates.push(toBlobUrl(`${FRAMES_CONTAINER}/${shop.id}/${sec.sectionId}/v1/frame_${pad4}.jpg`))
    if (idx !== 1) candidates.push(toBlobUrl(`${FRAMES_CONTAINER}/${shop.id}/${sec.sectionId}/v1/frame_${pad1}.jpg`))
    if (idx !== 0) candidates.push(toBlobUrl(`${FRAMES_CONTAINER}/${shop.id}/${sec.sectionId}/v1/frame_${pad0}.jpg`))

    // Mobile scan layout: scan-frames/{shopId}/sections/{sectionId}/frames/frame_XXXX.jpg
    candidates.push(toBlobUrl(`${FRAMES_CONTAINER}/${shop.id}/sections/${sec.sectionId}/frames/frame_${pad0}.jpg`))
    candidates.push(toBlobUrl(`${FRAMES_CONTAINER}/${shop.id}/${sec.sectionId}/frames/frame_${pad1}.jpg`))
  }

  // 4. Dataset candidate frames (scan-data container)
  const dataset = shop.dataset || storeShop?.dataset || useShopStore.getState().publicDatasets.find((d) => d.shopId === shop.id || d.id === shop.id)
  if (dataset) {
    const basePath = dataset.paths?.metadata
      ? dataset.paths.metadata.replace('/shop_metadata.json', '')
      : `/api/scan-data/${dataset.id}`
    candidates.push(toBlobUrl(resolveUrl(`${basePath}/thumbnail.jpg?shop=${dataset.id}`)))
    candidates.push(toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg`)))
    candidates.push(toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0001.jpg`)))
    candidates.push(toBlobUrl(resolveUrl(`${basePath}/frames/frame_0001.jpg`)))
  }

  // Deduplicate preserving order
  return Array.from(new Set(candidates.filter(Boolean)))
}

/**
 * Creates an onError handler for <img> elements that automatically steps through
 * candidate URLs until a valid frame is loaded, or calls onError callback when all fail.
 */
export function handleThumbnailImgError(
  e: React.SyntheticEvent<HTMLImageElement>,
  shop: ThumbnailCandidateShop,
  onFailed?: () => void
) {
  const img = e.currentTarget
  const candidates = getShopThumbnailCandidates(shop)
  const currentSrc = img.src

  // Find next untried candidate
  let currentIndex = -1
  for (let i = 0; i < candidates.length; i++) {
    const pathOnly = candidates[i].split('?')[0]
    if (currentSrc.includes(pathOnly)) {
      currentIndex = i
      break
    }
  }

  const nextIndex = currentIndex + 1
  if (nextIndex < candidates.length) {
    img.src = candidates[nextIndex]
  } else {
    onFailed?.()
  }
}
